/**
 * AnnouncementBadgeSubscriber — pushes drafts + upcoming counts via
 * SignalR (Communication hub).
 *
 * Phase 5 of the badge migration. Three Dashboards consumers are
 * already wired (AnnouncementCreated/Published/Archived) but the
 * publisher inside AnnouncementDomainService is deferred — see the
 * migration plan. Until publishers land, counters are driven entirely
 * by the REST baseline on mount + reconnect.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useAnnouncementService,
  useDomainDashboard,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useAnnouncementBadgesStore } from './announcementBadgesStore';

const ANNOUNCEMENT_ROLES = ['Admin'];
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[AnnouncementBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

interface AnnouncementDeltas {
  drafts?: number;
  upcoming?: number;
}

export function AnnouncementBadgeSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const enabled = hasAnyRole(ANNOUNCEMENT_ROLES);

  const service = useAnnouncementService();
  const setFromMetrics = useAnnouncementBadgesStore((s) => s.setFromMetrics);
  const applyDelta = useAnnouncementBadgesStore((s) => s.applyDelta);
  const reset = useAnnouncementBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    try {
      const items = ((await service.getViews()) as any[]) ?? [];
      const now = new Date();
      const drafts = items.filter((a: any) => {
        const s = (a?.statusName ?? '').toLowerCase();
        return s.includes('draft') || s.includes('pending');
      }).length;
      const upcoming = items.filter((a: any) => {
        if (!a?.activeFromDate) return false;
        return new Date(a.activeFromDate) > now;
      }).length;
      log('baseline', { drafts, upcoming });
      setFromMetrics({ drafts, upcoming });
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [service, setFromMetrics]);

  useEffect(() => {
    if (!enabled) return;
    refetchBaseline();
  }, [enabled, refetchBaseline]);

  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('communication', {
    autoConnect: enabled,
    autoSubscribe: false,
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<AnnouncementDeltas>) => {
      if (msg.domain !== 'Communication') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<AnnouncementDeltas>) => {
      if (msg.domain !== 'communication') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [enabled, isConnected, refetchBaseline, requestBadgeSnapshot]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { enabled });
  }, [enabled]);

  return null;
}
